import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';
import { AuthUser, LoginResponse } from '@/app/pages/auth/models/auth.model';
import { CookieService } from '@app/core/services/common/cookie.service';
import * as AuthActions from '@app/store/auth/auth.actions';
import * as AuthSelectors from '@app/store/auth/auth.selectors';
import { BaseApiService } from '@/app/core/services/http/base-api.service';
import { ApiResponse } from '@/app/core/models/api.model';
import {
  AccessTokenExpirationKey,
  RefreshTokenExpirationKey,
  TokenInfoKey,
} from '@/app/shared/constants/constant';
import { BizTypeUser } from '@/app/shared/constants/biz-type.enum';

/**
 * Authentication & Authorization Service with NgRx State Management
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseApiService {
  private readonly cookieService = inject(CookieService);

  // Observable streams from NgRx Store
  permissions$ = this.store.select(AuthSelectors.selectPermissions);
  isAuthenticated$ = this.store.select(AuthSelectors.selectIsAuthenticated);
  isLoading$ = this.store.select(AuthSelectors.selectIsLoading);
  error$ = this.store.select(AuthSelectors.selectError);
  accessToken$ = this.store.select(AuthSelectors.selectAccessToken);
  private accessToken: string | null = null;

  constructor() {
    super();
    // Initialize auth from sessionStorage on app start
    this.store.dispatch(AuthActions.initializeAuth());
    this.accessToken$.subscribe((value) => (this.accessToken = value));
  }

  /**
   * Get auth API URL - computed dynamically từ env config
   */
  private getApiURL(): string {
    return `${this.envService.getApiUrl()}/api/auth`;
  }

  // Synchronous getters (use firstValueFrom for immediate value)
  isLoggedIn(): boolean {
    // let result = false;
    // this.isAuthenticated$.pipe(first()).subscribe((value) => (result = value));
    return this.getToken() !== null && !this.hasExpiredAccessToken();
  }

  isAdmin(): boolean {
    // TODO: Implement admin check based on user roles or permissions
    let result = false;
    this.isAuthenticated$.pipe(first()).subscribe((value) => (result = value));
    return result;
  }

  getToken(): string | null {
    return this.cookieService.getCookie(TokenInfoKey);
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  /**
   * Login - Dispatch action to NgRx store
   */
  login(
    email: string,
    password: string,
    rememberMe: boolean,
    branchId?: number,
    branchCode?: string,
  ): void {
    this.store.dispatch(AuthActions.login({ email, password, rememberMe, branchId, branchCode }));
  }

  /**
   * Logout - Dispatch action to NgRx store
   */
  logout(navigateToLogin: boolean = true): void {
    this.store.dispatch(AuthActions.logout());
  }

  /**
   * Clear authentication errors
   */
  clearError(): void {
    this.store.dispatch(AuthActions.clearError());
  }

  /**
   * Get refresh token from secure cookie
   */
  getRefreshToken(): string | null {
    return this.cookieService.getRefreshToken();
  }

  /**
   * Check if refresh token exists
   */
  hasRefreshToken(): boolean {
    return this.cookieService.hasCookie('refresh_token');
  }

  /**
   * Refresh access token using refresh token from cookie
   */
  refreshAccessToken(): Observable<LoginResponse> {
    const accessToken = this.getToken();
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Lấy URL mỗi lần gọi để đảm bảo config mới nhất
    const apiURL = this.getApiURL();

    return this.http
      .post<ApiResponse<LoginResponse[]>>(apiURL + '/RefreshToken', {
        JsonHeader: {
          lang: this.i18n.getCurrentLang(),
          user: this.currentUser?.LoginName ?? '',
          channel: 'BACK',
          branchCode: this.currentUser?.branchCode ?? '',
          branchId: this.currentUser?.branchId ?? 0,
          OrganizationId_all: this.currentUser?.OrganizationId_all
        },
        AccessToken: accessToken,
        RefreshToken: refreshToken,
      })
      .pipe(
        map((response: ApiResponse<LoginResponse[]>) => {
          // Dispatch action to update auth state with new token
          this.store.dispatch(AuthActions.loginSuccess({ response }));
          if (response.Data && response.Data.length > 0) {
            return response.Data[0];
          }
          throw new Error('Invalid refresh token response');
        }),
        catchError((error) => {
          throw error;
        }),
      );
  }

  hasExpiredRefreshToken(): boolean {
    const refreshTokenExpiration = this.cookieService.getCookie(RefreshTokenExpirationKey);
    if (!refreshTokenExpiration) {
      return true;
    }
    return new Date(refreshTokenExpiration) < new Date();
  }

  hasExpiredAccessToken(): boolean {
    const accessTokenExpiration = this.cookieService.getCookie(AccessTokenExpirationKey);
    if (!accessTokenExpiration) {
      return true;
    }
    return new Date(accessTokenExpiration) < new Date();
  }

  /**
   * Check if user has a specific permission (sync)
   */
  hasPermission(permissionCode: string): boolean {
    let result = false;
    this.store
      .select(AuthSelectors.selectHasPermission(permissionCode))
      .pipe(first())
      .subscribe((value: boolean) => (result = value));
    return result;
  }

  /**
   * Refresh user information from server using SYS_AUTH_USER_VIEW API
   * This method will update user info, menus, and functions
   */
  refreshUserInfo(userId?: number, loginName?: string): Observable<ApiResponse<any>> {
    // Use current user info if not provided
    const currentId = userId || this.currentUser?.UserId;
    const loginNameToUse = loginName || this.currentUser?.LoginName;

    if (!currentId || !loginNameToUse) {
      throw new Error('User ID and Login Name are required for refresh');
    }

    const jsonParam = {
      ID: currentId,
      C_LOGIN_NAME: loginNameToUse,
    };

    return this.executePost(BizTypeUser.SYS_AUTH_USER_VIEW, jsonParam);
  }

  public executePost(bizType: string, jsonParam: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      this.envService.getApiUrl() + '/api/business/add-user',
      {
        JsonHeader: {
          lang: this.i18n.getCurrentLang(),
          user: this.currentUser?.LoginName ?? '',
          channel: 'BACK',
          branchCode: this.currentUser?.branchCode ?? '',
          branchId: this.currentUser?.branchId ?? 0,
          OrganizationId_all:this.currentUser?.OrganizationId_all
        },
        BizType: bizType,
        JsonParam: jsonParam,
      },
    );
  }
}
